import { useCallback, useEffect, useMemo, useState } from "react";

import {
  type AWSNodeConfigApiParameters,
  type CloudFlowNodeType,
  CloudFlowProvider,
  type NodeConfigApiParameters,
} from "@doitintl/cmp-models";
import axios from "axios";

import { useApiContext } from "../../../../api/context";
import { cloudflowTexts } from "../../../../assets/texts";
import { useCustomerId } from "../../../../Components/hooks/useCustomerId";
import { useErrorSnackbar } from "../../../../Components/SharedSnackbar/SharedSnackbar.context";
import { consoleErrorWithSentry } from "../../../../utils";
import { useAWSAccounts } from "../../../Settings/AWS/hooks";
import { AccountStatus } from "../../../Settings/AWS/types";
import {
  type AWSCloudPermissions,
  type GCPCloudPermissions,
  type NodeConfigs,
  type PermissionCheckResult,
  type PermissionMap,
  PermissionValidityStatus,
} from "../../types";
import { type NodeWitOutputModel } from "../ApiActionParametersForm/parameters/wrappers/ReferencedField/useReferencedFieldContext";
import { useCloudflowState } from "../Common/hooks/useCloudflowState";
import { getOperationByPointer, useGetOperationById } from "../Common/hooks/useGetOperationById";
import { useGetServiceDescriptor } from "../Common/hooks/useGetServiceDescriptor";
import { useReferenceableNodes } from "../Common/hooks/useReferenceableNodes";
import { getAWSPermissionToCheck } from "../utils/awsUtils";
import { GCPHierarchicalResource, getGCPPermissions, parseServiceAccountResource } from "../utils/gcpUtils";
import { useAWSPermissionsCheck, useCheckAllAccountPermissions } from "./hooks/useAWSPermissionsCheck";
import { useNodeConfigurationContext } from "./NodeConfigurationContext";
import { useAWSWaiters } from "./Tabs/WaiterConfig/useAWSWaiters";

export type GCPPermissionConfigValues = {
  serviceAccountResource: string;
  hierarchicalResource: GCPHierarchicalResource;
  [key: string]: string | null | undefined;
};

const validateAPIParams = (parameters: NodeConfigApiParameters) => {
  if (!parameters) {
    consoleErrorWithSentry("parameters are required for permissions validation");
    return false;
  }
  switch (parameters.provider) {
    case "AWS":
      if (!parameters.configurationValues?.accountId || typeof parameters.configurationValues?.accountId !== "string") {
        consoleErrorWithSentry("accountId is required for AWS permissions validation");
        return false;
      }
      break;
    case "GCP":
      if (typeof parameters.configurationValues?.serviceAccountResource !== "string") {
        consoleErrorWithSentry("Organization or project is required for GCP permissions validation");
        return false;
      }
      break;
    default:
      consoleErrorWithSentry(`Unsupported provider: ${parameters.provider}`);
      return false;
  }

  return true;
};

export const useAWSCloudPermissions = (): AWSCloudPermissions => {
  const { isBlueprint, isDemoFlow } = useCloudflowState();
  const { nodeConfig, updateNode } = useNodeConfigurationContext<CloudFlowNodeType.ACTION>();
  const { serviceData, loading: isServiceDescriptorLoading } = useGetServiceDescriptor(nodeConfig.parameters.operation);
  const { operationData, loading: isOperationLoading } = useGetOperationById(nodeConfig.parameters.operation);
  const parameters = nodeConfig.parameters as AWSNodeConfigApiParameters;
  const { waiters } = useAWSWaiters(nodeConfig.parameters.operation.service, nodeConfig.parameters.operation.version);
  const selectedWaiter = useMemo(
    () => waiters?.find((waiter) => waiter.id === parameters.waiterConfiguration?.waiter) || null,
    [waiters, parameters.waiterConfiguration]
  );

  const showError = useErrorSnackbar(7);
  const [accounts, _, accountsLoading] = useAWSAccounts();
  const [result, setResult] = useState<PermissionCheckResult>({
    data: undefined,
    status: PermissionValidityStatus.INITIAL,
  });
  const [loading, setLoading] = useState<boolean>(false);

  const checkAWSPermissions = useAWSPermissionsCheck(nodeConfig.flowId);
  const checkAllAWSPermissions = useCheckAllAccountPermissions(nodeConfig.flowId);

  const updatePermissionsError = useCallback(
    (valid: boolean) => {
      updateNode((prevNode) => {
        const errors = { ...prevNode.errors };
        if (valid) {
          delete errors.permission_error;
        } else {
          errors.permission_error = cloudflowTexts.STATUS.PERMISSION_ERROR;
        }

        return { errors };
      });
    },
    [updateNode]
  );

  const updatePermissions = useCallback(
    async (accountId: string) => {
      if (isBlueprint || isDemoFlow) {
        setResult({ status: PermissionValidityStatus.VALID, data: undefined });
        return;
      }

      if (!operationData?.operationPointer?.id || !serviceData?.permissionsNamespace) {
        return;
      }

      setLoading(true);
      try {
        const permissionsToCheck = getAWSPermissionToCheck(
          operationData.operationPointer.id,
          serviceData.permissionsNamespace,
          operationData.operation?.permissions
        );

        if (selectedWaiter) {
          const waiterOperation = await getOperationByPointer({
            id: selectedWaiter.operationModel.id,
            service: nodeConfig.parameters.operation.service,
            version: nodeConfig.parameters.operation.version,
            provider: nodeConfig.parameters.operation.provider,
          });

          permissionsToCheck.push(
            ...getAWSPermissionToCheck(
              selectedWaiter.operationModel.id,
              serviceData.permissionsNamespace,
              waiterOperation.permissions
            )
          );
        }

        if (accountId === "All accounts") {
          const validAccounts = accounts.filter((a) => a.status === AccountStatus.healthy);
          if (validAccounts.length === 0) {
            throw new Error("no connected AWS accounts selected");
          }

          const res = await checkAllAWSPermissions(
            accounts.filter((a) => a.status === AccountStatus.healthy),
            permissionsToCheck
          );

          const hasFailedCheck = res.some((r) => !r.checkResult.permissionsExists);
          if (!hasFailedCheck) {
            setResult({ status: PermissionValidityStatus.VALID, data: undefined });
            updatePermissionsError(!hasFailedCheck);
          } else {
            setResult({
              status: PermissionValidityStatus.INVALID,
              data: res.map((r) => ({
                accountName: r.accountName,
                command: r.checkResult.commandToGrantPermission,
                requiredPermissions: r.checkResult.missingPermissions,
                hasPermission: r.checkResult.permissionsExists,
              })),
            });
            updatePermissionsError(!hasFailedCheck);
          }
        } else {
          const account = accounts.find((a) => a.status === AccountStatus.healthy && a.accountId === accountId);

          if (!account) {
            throw new Error("selected AWS Account not found");
          }

          const { permissionsExists, missingPermissions, commandToGrantPermission } = await checkAWSPermissions(
            account,
            permissionsToCheck
          );

          if (!permissionsExists) {
            setResult({
              data: { requiredPermissions: missingPermissions, command: commandToGrantPermission },
              status: PermissionValidityStatus.INVALID,
            });
          } else {
            setResult({ data: undefined, status: PermissionValidityStatus.VALID });
          }

          updatePermissionsError(permissionsExists);
        }
      } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        showError(`Failed to verify AWS permissions, error: ${errorMessage}`);
        consoleErrorWithSentry(error);
      } finally {
        setLoading(false);
      }
    },
    [
      isBlueprint,
      isDemoFlow,
      operationData,
      serviceData,
      selectedWaiter,
      nodeConfig,
      accounts,
      checkAllAWSPermissions,
      updatePermissionsError,
      checkAWSPermissions,
      showError,
    ]
  );

  return {
    result,
    updatePermissions,
    loading: loading || accountsLoading || isServiceDescriptorLoading || isOperationLoading,
  };
};

export const useGCPCloudPermissions = ({
  permissionConfigValues,
}: {
  permissionConfigValues: GCPPermissionConfigValues;
}): GCPCloudPermissions => {
  const { isBlueprint, isDemoFlow } = useCloudflowState();
  const { nodeConfig, updateNode } = useNodeConfigurationContext<CloudFlowNodeType.ACTION>();
  const { operationData, loading: isOperationLoading } = useGetOperationById(nodeConfig.parameters.operation);
  const permissions = getGCPPermissions(nodeConfig.parameters.operation, operationData.operation?.permissions);

  const customerId = useCustomerId();
  const api = useApiContext();
  const showError = useErrorSnackbar(7);
  const [result, setResult] = useState<PermissionCheckResult>({
    data: undefined,
    status: PermissionValidityStatus.INITIAL,
  });
  const [loading, setLoading] = useState<boolean>(false);

  const validateGCPPermissions = useCallback(
    async ({
      customerId,
      serviceAccountResource,
      permissions,
      projectId,
    }: {
      customerId: string;
      serviceAccountResource: string;
      permissions: string[];
      projectId?: string;
    }) => {
      const response = await api.request<PermissionMap>({
        method: "POST",
        url: `/v1/customers/${customerId}/cloudflow/validate-gcp-permissions`,
        data: {
          serviceAccountResource,
          permissions,
          projectId,
          scopes: ["https://www.googleapis.com/auth/cloud-platform"],
        },
      });
      return response.data;
    },
    [api]
  );

  function getGCPCommand(
    nodeConfig: NodeConfigs<CloudFlowNodeType.ACTION>,
    permissions: string[],
    permissionConfigValues: GCPPermissionConfigValues
  ): string {
    if (nodeConfig?.parameters?.provider !== CloudFlowProvider.GCP) {
      throw new Error("Provider not supported");
    }

    const perms = Array.isArray(permissions) ? permissions : [permissions];
    if (perms.length === 0) return "";

    const { serviceAccountResource, hierarchicalResource, permissionsProjectId } = permissionConfigValues;

    const [resourceType, resourceId] = parseServiceAccountResource(serviceAccountResource);

    let scopeFlag = "";
    if (hierarchicalResource === GCPHierarchicalResource.PROJECT && permissionsProjectId) {
      scopeFlag = `--project ${permissionsProjectId}`;
    } else if (
      hierarchicalResource === GCPHierarchicalResource.ORGANIZATION &&
      resourceType === "organizations" &&
      resourceId
    ) {
      scopeFlag = `--organization ${resourceId}`;
    } else {
      return "";
    }

    const joined = perms.join(",");
    return `gcloud iam roles update doit_cmp_role ${scopeFlag} --add-permissions ${joined}`;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const updatePermissionsError = useCallback(
    (valid: boolean) => {
      updateNode((prevNode) => {
        const errors = { ...prevNode.errors };
        if (valid) {
          delete errors.permission_error;
        } else {
          errors.permission_error = cloudflowTexts.STATUS.PERMISSION_ERROR;
        }

        return { errors };
      });
    },
    [updateNode]
  );

  const updatePermissions = useCallback(async () => {
    if (isBlueprint || isDemoFlow) {
      setResult({ status: PermissionValidityStatus.VALID, data: undefined });
      return;
    }
    if (!nodeConfig.parameters) {
      return;
    }

    if (!validateAPIParams(nodeConfig.parameters)) {
      showError("Invalid parameters for permissions validation");
      return;
    }

    if (nodeConfig.parameters.provider !== "GCP") {
      consoleErrorWithSentry("Invalid provider for GCP permissions hook");
      return;
    }

    setLoading(true);
    try {
      const projectId = permissionConfigValues.permissionsProjectId;

      const permissionMap = await validateGCPPermissions({
        customerId,
        serviceAccountResource: nodeConfig.parameters.configurationValues.serviceAccountResource,
        permissions,
        ...(projectId && {
          projectId,
        }),
      });

      if (!permissionMap) {
        setResult({ status: PermissionValidityStatus.UNABLE_TO_VERIFY, data: undefined });
      } else {
        const filteredPermissions = Object.entries(permissionMap)
          .filter(([, value]) => !value)
          .map(([key]) => key);

        if (filteredPermissions.length > 0) {
          const command = getGCPCommand(nodeConfig, filteredPermissions, permissionConfigValues);

          setResult({
            status: PermissionValidityStatus.INVALID,
            data: { requiredPermissions: filteredPermissions, command },
          });
        } else {
          setResult({ status: PermissionValidityStatus.VALID, data: undefined });
        }

        // disable updating node permission error for GCP
        // updatePermissionsError(filteredPermissions.length === 0);
      }
    } catch (error: unknown) {
      if (axios.isAxiosError(error) && error.response?.status === 403) {
        showError(
          "Service account could not be impersonated. Please check that the service account has the required permissions."
        );
      } else {
        const errorMessage = error instanceof Error ? error.message : String(error);
        showError(`Failed to verify GCP permissions, error: ${errorMessage}`);
      }

      consoleErrorWithSentry(error);
    } finally {
      setLoading(false);
    }
  }, [
    isBlueprint,
    isDemoFlow,
    nodeConfig,
    showError,
    permissionConfigValues,
    validateGCPPermissions,
    customerId,
    permissions,
  ]);

  return { result, updatePermissions, loading: loading || isOperationLoading };
};

export const useNodeDataSourceUpdate = ({ nodeId, referencedNodeId }: { nodeId: string; referencedNodeId: string }) => {
  const { referenceableNodes } = useReferenceableNodes(nodeId);

  const [selectedNode, setSelectedNode] = useState<NodeWitOutputModel | undefined>();
  const [switchDataSourceOpen, setSwitchDataSourceOpen] = useState(false);
  const [handleConfirm, setHandleConfirm] = useState<(() => Promise<any>) | undefined>();
  const [handleClose, setHandleClose] = useState<(() => Promise<any>) | undefined>();

  useEffect(() => {
    const preselectedNode = referenceableNodes.find((node) => node.id === referencedNodeId);
    setSelectedNode(preselectedNode);
  }, [referencedNodeId, referenceableNodes]);

  const confirmSwitchDataSource = useCallback(
    async (onConfirm: (() => Promise<void>) | undefined, onClose: (() => Promise<void>) | undefined): Promise<void> => {
      setSwitchDataSourceOpen(true);
      const handleConfirmWrapper = async () => {
        setSwitchDataSourceOpen(false);
        if (onConfirm) {
          await onConfirm();
        }
      };
      const handleCloseWrapper = async () => {
        setSwitchDataSourceOpen(false);
        if (onClose) {
          await onClose();
        }
      };
      setHandleConfirm(() => handleConfirmWrapper);
      setHandleClose(() => handleCloseWrapper);
    },
    []
  );

  return {
    selectedNode,
    setSelectedNode,
    referenceableNodes,
    switchDataSourceOpen,
    setSwitchDataSourceOpen,
    handleConfirm,
    handleClose,
    confirmSwitchDataSource,
  };
};
